import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-76df1fa0"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("strong", null, "Compare these phenotypes ...", -1))
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("strong", null, "... to", -1))
const _hoisted_3 = /*#__PURE__*/_createTextVNode("Running analysis")
const _hoisted_4 = /*#__PURE__*/_createTextVNode("Error running analysis")
const _hoisted_5 = { class: "primary truncate" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "secondary truncate" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("strong", null, "Phenotype Similarity Comparison", -1))
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { id: "phenogrid" }, null, -1))

import { ref, watch, onMounted } from "vue";
import { startCase, kebabCase, isEqual } from "lodash";
import AppSelectTags from "@/components/AppSelectTags.vue";
import AppSelectSingle from "@/components/AppSelectSingle.vue";
import AppRing from "@/components/AppRing.vue";
import {
  compareSetToTaxon,
  compareSetToSet,
  getPhenotypes,
} from "@/api/phenotype-explorer";
import { Option, Options } from "@/components/AppSelectTags";
import { snackbar } from "@/components/TheSnackbar";
import { mountPhenogrid } from "@/api/phenogrid";
import { useQuery } from "@/util/composables";
import { parse } from "@/util/object";
import examples from "./phenotype-explorer.json";

/** common tooltip explaining how to use multi-select component */
interface GeneratedFrom {
  /** the option (gene/disease/phenotype) that the phenotypes came from */
  option?: Option;
  /** the phenotypes themselves */
  options?: Options;
}

/** first set of phenotypes */

export default /*#__PURE__*/_defineComponent({
  __name: 'PhenotypeExplorer',
  setup(__props) {

const multiTooltip = `In this box, you can select phenotypes in 3 ways:<br>
  <ol>
    <li>Search for individual phenotypes</li>
    <li>Search for genes/diseases and get their associated phenotypes</li>
    <li>Paste comma-separated phenotype IDs</li>
  </ol>`;

/** options for mode of second set */
const bModeOptions = [
  { id: "these phenotypes ..." },
  { id: "phenotypes from all genes of ..." },
  { id: "phenotypes from all human diseases" },
];

/**
 * small hard-coded list of taxon options, just for phenotype explorer, so no
 * backend querying needed
 */
const taxons = [
  { id: "9606", name: "human", scientific: "Homo sapiens" },
  { id: "10090", name: "mouse", scientific: "Mus musculus" },
  { id: "7955", name: "zebrafish", scientific: "Danio rerio" },
  { id: "7227", name: "fruitfly", scientific: "Drosophila melanogaster" },
  { id: "6239", name: "worm", scientific: "Caenorhabditis elegans" },
  { id: "8353", name: "frog", scientific: "Xenopus" },
];

const bTaxonHuman = taxons[0];

/** taxon options for second set */
const bTaxonOptions = taxons.slice(1);

/** example data */
const aPhenotypes = ref([] as Options);
/** "generated from" helpers after selecting gene or disease */
const aGeneratedFrom = ref({} as GeneratedFrom);
/** selected mode of second set */
const bMode = ref(bModeOptions[0]);
/** selected taxon for second set */
const bTaxon = ref(bTaxonOptions[0]);
/** second set of phenotypes */
const bPhenotypes = ref([] as Options);
/** "generated from" helpers after selecting gene or disease */
const bGeneratedFrom = ref({} as GeneratedFrom);

/** example phenotype set comparison */
function doExample() {
  aPhenotypes.value = examples.a.options;
  bPhenotypes.value = examples.b.options;
  aGeneratedFrom.value = examples.a;
  bGeneratedFrom.value = examples.b;
}

/** comparison analysis */
const {
  query: runAnalysis,
  data: comparison,
  isLoading,
  isError,
} = useQuery(
  async function () {
    /** run appropriate analysis based on selected mode */
    if (bMode.value.id.includes("these phenotypes"))
      return await compareSetToSet(
        aPhenotypes.value.map(({ id }) => id),
        bPhenotypes.value.map(({ id }) => id)
      );
    else
      return await compareSetToTaxon(
        aPhenotypes.value.map(({ id }) => id),
        bMode.value.id.includes("diseases") ? bTaxonHuman.id : bTaxon.value.id
      );
  },

  /** default value */
  { matches: [] }
);

/** when multi select component runs spread options function */
function spreadOptions(option: Option, options: Options, set: string) {
  /** notify */
  if (options.length === 0) snackbar("No associated phenotypes found");
  else snackbar(`Selected ${options.length} phenotypes`);

  /** set "generated from" helpers */
  if (set === "a") aGeneratedFrom.value = { option, options };
  else if (set === "b") bGeneratedFrom.value = { option, options };
}

/** show phenogrid results */
function runPhenogrid() {
  /** which biolink /sim endpoint to use */
  const mode = bMode.value.id.includes("these phenotypes")
    ? "compare"
    : "search";

  /** use first group of phenotypes for y axis */
  const yAxis = aPhenotypes.value;

  /** use second taxon id or group of phenotypes as x axis */
  let xAxis = [];
  if (mode === "compare") xAxis = bPhenotypes.value;
  else {
    const taxon = bMode.value.id.includes("diseases")
      ? bTaxonHuman
      : bTaxon.value;
    xAxis = [{ id: taxon.id, name: taxon.scientific }];
  }
  /** call phenogrid */
  mountPhenogrid("#phenogrid", xAxis, yAxis, mode);
}

/** run phenogrid, attach to div container */
watch(
  () => comparison.value,
  () => {
    if (comparison.value.matches.length) runPhenogrid();
  }
);

/** clear/reset results */
function clearResults() {
  comparison.value = { matches: [] };
}

/** get description to show below phenotypes select box */
function description(
  phenotypes: Options,
  generatedFrom: GeneratedFrom
): string {
  const description = [];

  /** number of phenotypes */
  description.push(`${phenotypes.length} selected`);

  /** to avoid misleading text, only show if lists match exactly */
  if (isEqual(generatedFrom.options, phenotypes))
    description.push(
      `generated from "${
        generatedFrom.option?.name || generatedFrom.option?.id
      }"`
    );
  return `(${description.join(", ")})`;
}

/** clear results when inputs are changed to avoid de-sync */
watch([aPhenotypes, bMode, bTaxon, bPhenotypes], clearResults, { deep: true });

/** fill in phenotype ids from text annotator */
onMounted(() => {
  if (window.history.state.phenotypes) {
    const phenotypes = parse(window.history.state.phenotypes, []) as Options;
    aPhenotypes.value = phenotypes;
    aGeneratedFrom.value = {
      option: { id: "text annotator" },
      options: phenotypes,
    };
  }
});

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _component_AppSection = _resolveComponent("AppSection")!
  const _component_AppStatus = _resolveComponent("AppStatus")!
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _component_AppLink = _resolveComponent("AppLink")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppButton, {
          text: "Try an example",
          design: "small",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (doExample()))
        }),
        _hoisted_1,
        _createVNode(AppSelectTags, {
          modelValue: aPhenotypes.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((aPhenotypes).value = $event)),
          name: "First set of phenotypes",
          options: _unref(getPhenotypes),
          placeholder: "Select phenotypes",
          tooltip: multiTooltip,
          description: description(aPhenotypes.value, aGeneratedFrom.value),
          onSpreadOptions: _cache[2] || (_cache[2] = (option, options) => spreadOptions(option, options, 'a'))
        }, null, 8, ["modelValue", "options", "description"]),
        _createVNode(_component_AppFlex, { gap: "small" }, {
          default: _withCtx(() => [
            _hoisted_2,
            _createVNode(AppSelectSingle, {
              modelValue: bMode.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((bMode).value = $event)),
              name: "Second set mode",
              options: bModeOptions
            }, null, 8, ["modelValue"]),
            (bMode.value.id.includes('genes'))
              ? (_openBlock(), _createBlock(AppSelectSingle, {
                  key: 0,
                  modelValue: bTaxon.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((bTaxon).value = $event)),
                  name: "Second set taxon",
                  options: _unref(bTaxonOptions)
                }, null, 8, ["modelValue", "options"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        (bMode.value.id.includes('these phenotypes'))
          ? (_openBlock(), _createBlock(AppSelectTags, {
              key: 0,
              modelValue: bPhenotypes.value,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((bPhenotypes).value = $event)),
              name: "Second set of phenotypes",
              options: _unref(getPhenotypes),
              placeholder: "Select phenotypes",
              tooltip: multiTooltip,
              description: description(bPhenotypes.value, bGeneratedFrom.value),
              onSpreadOptions: _cache[6] || (_cache[6] = (option, options) => spreadOptions(option, options, 'b'))
            }, null, 8, ["modelValue", "options", "description"]))
          : _createCommentVNode("", true),
        _createVNode(_component_AppButton, {
          text: "Analyze",
          icon: "bars-progress",
          onClick: _unref(runAnalysis)
        }, null, 8, ["onClick"])
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        (_unref(isLoading))
          ? (_openBlock(), _createBlock(_component_AppStatus, {
              key: 0,
              code: "loading"
            }, {
              default: _withCtx(() => [
                _hoisted_3
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(isError))
          ? (_openBlock(), _createBlock(_component_AppStatus, {
              key: 1,
              code: "error"
            }, {
              default: _withCtx(() => [
                _hoisted_4
              ]),
              _: 1
            }))
          : (_unref(comparison).matches.length)
            ? (_openBlock(), _createBlock(_component_AppFlex, { key: 2 }, {
                default: _withCtx(() => [
                  _createElementVNode("strong", null, "Top " + _toDisplayString(Math.min(_unref(comparison).matches.length, 10)) + " match(es)", 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(comparison).matches.slice(0, 10), (match, matchIndex) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: matchIndex,
                      class: "match"
                    }, [
                      _withDirectives(_createVNode(AppRing, {
                        score: match.score,
                        min: _unref(comparison).minScore,
                        max: _unref(comparison).maxScore
                      }, null, 8, ["score", "min", "max"]), [
                        [_directive_tooltip, 'Similarity score']
                      ]),
                      _createVNode(_component_AppFlex, {
                        direction: "col",
                        "h-align": "stretch",
                        gap: "small",
                        class: "details"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_5, [
                            _withDirectives(_createVNode(_component_AppIcon, {
                              icon: `category-${match.category}`
                            }, null, 8, ["icon"]), [
                              [_directive_tooltip, _unref(startCase)(match.category)]
                            ]),
                            (match.name.includes(' + '))
                              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(match.name.split(' + '), (id, idIndex) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, { key: idIndex }, [
                                    _createVNode(_component_AppLink, {
                                      to: `/${_unref(kebabCase)(match.category)}/${id}`
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(id), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["to"]),
                                    (idIndex !== match.name.split(' + ').length - 1)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_6, " + "))
                                      : _createCommentVNode("", true)
                                  ], 64))
                                }), 128))
                              : (_openBlock(), _createBlock(_component_AppLink, {
                                  key: 1,
                                  to: `/${_unref(kebabCase)(match.category)}/${match.id}`
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(match.name), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["to"]))
                          ]),
                          _createElementVNode("div", _hoisted_7, [
                            _createElementVNode("span", null, _toDisplayString(match.id), 1),
                            (match.taxon)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_8, "  |  " + _toDisplayString(match.taxon), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 2
                      }, 1024)
                    ]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
        (_unref(comparison).matches.length)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _hoisted_9,
              _hoisted_10
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})